import { useCallback } from 'react';

import { useIspmContext } from '../context';
import { useChatOverlayContext } from '../context/chatOverlayContext';
import getDashboardList from '../data-layer/dashboard-list/api.service';
import { saveChartToDashboard } from '../data-layer/save-chart-to-dashboard';
import { type SaveChartRequest } from '../data-layer/types';
import { getSaviApiUrlLists, Logger } from '../services';

import useMessageHandlers from './use-messagehandlers';

/**
 * Custom hook used by component to get access to api calls.
 * @returns
 */
const useDashboardApi = () => {
  const { ispmState } = useIspmContext();
  const { updateChatOverlayState } = useChatOverlayContext();
  const { DASHBOARD_LIST_URL } = getSaviApiUrlLists(
    ispmState.useMockServerApis
  );
  const {
    updateAutomatedApiResponseInSession,
    updateAutomatedUserMessageInSession,
  } = useMessageHandlers();

  const fetchDashboards = useCallback(() => {
    getDashboardList(DASHBOARD_LIST_URL)
      .then((data) => {
        if ('dashboards' in data) {
          updateChatOverlayState('dashboardLists', data.dashboards);
        }
      })
      .catch((err: unknown) => {
        Logger.error({ err });
      });
  }, [DASHBOARD_LIST_URL, updateChatOverlayState]);

  const saveChart = useCallback(
    async (request: SaveChartRequest, url: string) => {
      try {
        updateAutomatedUserMessageInSession(
          'Save to the Intelligence Dashboard'
        );
        updateChatOverlayState('isApiCallInProgress', true);
        updateChatOverlayState('inputDisabled', true);
        const response = await saveChartToDashboard(request, url);

        updateChatOverlayState('isApiCallInProgress', false);
        updateChatOverlayState('inputDisabled', false);

        // Localization is happening inside this method
        updateAutomatedApiResponseInSession(
          'This chart has been pinned to your dashboard',
          {
            isSaveToChart: true,
          }
        );

        return response;
      } catch (err) {
        Logger.error({ err });
        updateChatOverlayState('isApiCallInProgress', false);
        updateChatOverlayState('inputDisabled', false);

        // Localization is happening inside this method
        updateAutomatedApiResponseInSession(
          'Sorry, there was an error or interruption while processing your prompt. When the service becomes available again, you can either enter your original prompt or try a new one'
        );

        throw err;
      }
    },
    [
      updateAutomatedApiResponseInSession,
      updateAutomatedUserMessageInSession,
      updateChatOverlayState,
    ]
  );

  return {
    fetchDashboards,
    saveChart,
  };
};

export default useDashboardApi;
